import type { LocationsTags } from '@workspace/types'
import type { InternalCFACL, InternalCFLan, InternalCFWan, MagicWanSite } from '@workspace/types/cf'
import type { MerakiData, MerakiVlanResponse } from '@workspace/types/meraki'
import { sql } from 'drizzle-orm'
import {
  bigint,
  bigserial,
  boolean,
  check,
  date,
  foreignKey,
  index,
  inet,
  integer,
  jsonb,
  macaddr,
  pgEnum,
  pgTable,
  pgView,
  primaryKey,
  real,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core'
import type {
  GenericJsonb,
  OrgCfData,
  OrgCradlepointData,
  OrgExtremeData,
  OrgIntuneData,
  OrgMerakiData,
  OrgMetadata,
  OrgPrivateMetadata,
  OrgPublicMetadata,
} from './extra'
import { createId } from './utils'

export const connectorActiveStatus = pgEnum('connector_active_status', ['ACTIVE', 'INACTIVE'])
export const edgeVendor = pgEnum('edge_vendor', ['DELL', 'CISCO', 'MERAKI'])
export const siteType = pgEnum('site_type', ['EDGE', 'SWITCH', 'AUXILIARY', 'DATACENTER'])
export const locationTypeEnum = pgEnum('location_type', [
  'Store',
  'Distribution Center',
  'Warehouse',
  'Head Office',
  'Office',
  'Data Center',
  'Cloud',
  'Other',
])
export const networkMigrationStatus = pgEnum('network_migration_status', ['PENDING', 'MIGRATED', 'IN_PROGRESS'])
export const nodeKindEnum = pgEnum('node_kind', [
  'WORKSTATION',
  'AUXILIARY',
  'UNKNOWN',
  'AP',
  'ROUTER',
  'ROUTER_AS_L2_VPN_GATEWAY',
  'ROUTER_AS_L3_VPN_GATEWAY',
  'SWITCH',
  'SWITCH_HAC',
  'SWITCH_DELL',
  'L2_VPN_GATEWAY',
  'L3_VPN_GATEWAY',
  'COMPUTE',
])
export type NodeKind = (typeof nodeKindEnum.enumValues)[number]
export const switchVendor = pgEnum('switch_vendor', ['EXTREME', 'CISCO', 'MERAKI'])
export const cgVendor = pgEnum('cg_vendor', ['MERAKI', 'CRADLEPOINT'])
export const lifecycleState = pgEnum('lifecycle_state', [
  'CONFIGURING',
  'DISPATCHING',
  'PROVISIONED',
  'CLOSED',
  'UNMANAGED',
])
export type LifecycleState = (typeof lifecycleState.enumValues)[number]

export const wanTypeEnum = pgEnum('wan_type', ['PRIMARY', 'SECONDARY', 'TERTIARY'])
export const wanAssignmentTypeEnum = pgEnum('wan_assignment_type', ['STATIC', 'DHCP'])
export const wanSourceEnum = pgEnum('wan_source', ['Vector', 'Meraki', 'Cloudflare', 'Fortinet', 'Cradlepoint'])

export const terminals = pgTable(
  'terminals',
  {
    id: varchar({ length: 255 }).$defaultFn(createId).primaryKey().notNull(),
    companyId: varchar('company_id', { length: 255 }).notNull(),
    merchantId: varchar('merchant_id', { length: 255 }),
    adyenStoreId: varchar('adyen_store_id', { length: 255 }),
    locationId: varchar('location_id', { length: 255 }),
    deviceId: varchar('device_id', { length: 255 }),
    name: varchar({ length: 255 }).notNull(),
    model: varchar({ length: 255 }).notNull(),
    status: varchar({ length: 255 }).notNull(),
    serialNumber: varchar('serial_number', { length: 255 }).notNull(),
    firmwareVersion: varchar('firmware_version', { length: 255 }),
    cellularIccid: varchar('cellular_iccid', { length: 255 }),
    cellularStatus: varchar('cellular_status', { length: 255 }),
    wifiIpAddress: inet('wifi_ip_address'),
    wifiMacAddress: macaddr('wifi_mac_address'),
    ethernetMacAddress: macaddr('ethernet_mac_address'),
    ethernetIpAddress: inet('ethernet_ip_address'),
    ethernetLinkNegotiation: varchar('ethernet_link_negotiation', { length: 255 }),
    bluetoothMacAddress: macaddr('bluetooth_mac_address'),
    bluetoothIpAddress: inet('bluetooth_ip_address'),
    lastActivityAt: varchar('last_activity_at', { length: 255 }),
    lastTransactionAt: varchar('last_transaction_at', { length: 255 }),
    restartLocalTime: varchar('restart_local_time', { length: 255 }),
    createdAt: timestamp('created_at', { precision: 6, withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp('updated_at', { precision: 6, withTimezone: true, mode: 'string' }),
    businessUnit: varchar('business_unit', { length: 255 }),
    workstationId: varchar('workstation_id', { length: 255 }),
  },
  (table) => [
    index('idx_adyen_store_id').using('btree', table.adyenStoreId.asc().nullsLast().op('text_ops')),
    index('idx_firmware_version').using('btree', table.firmwareVersion.asc().nullsLast().op('text_ops')),
    index('idx_location_id').using('btree', table.locationId.asc().nullsLast().op('text_ops')),
    index('idx_model').using('btree', table.model.asc().nullsLast().op('text_ops')),
    index('idx_name').using('btree', table.name.asc().nullsLast().op('text_ops')),
    index('idx_serial_number').using('btree', table.serialNumber.asc().nullsLast().op('text_ops')),
    index('idx_status').using('btree', table.status.asc().nullsLast().op('text_ops')),
    index('idx_merchant_id_last_activity_at').using(
      'btree',
      table.merchantId.asc().nullsLast().op('text_ops'),
      table.lastActivityAt.asc().nullsLast().op('text_ops'),
    ),
    index('idx_merchant_id_status').using(
      'btree',
      table.merchantId.asc().nullsLast().op('text_ops'),
      table.status.asc().nullsLast().op('text_ops'),
    ),
    foreignKey({
      columns: [table.locationId],
      foreignColumns: [locations.id],
      name: 'terminals_location_id_locations_id_fk',
    }),
    unique('terminals_serial_number_unique').on(table.serialNumber),
  ],
)

export const purchaseOrders = pgTable(
  'purchase_orders',
  {
    id: varchar().primaryKey().notNull(),
    poNum: integer('po_num'),
    vendor: varchar().notNull(),
    requestor: varchar().notNull(),
    reason: varchar().notNull(),
    amt: real().notNull(),
    createdAt: timestamp('created_at', { precision: 6, withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp('updated_at', { precision: 6, withTimezone: true, mode: 'string' }),
    deletedAt: timestamp('deleted_at', { precision: 6, withTimezone: true, mode: 'string' }),
  },
  (table) => [
    index('idx_requestor').using('btree', table.requestor.asc().nullsLast().op('text_ops')),
    index('idx_vendor').using('btree', table.vendor.asc().nullsLast().op('text_ops')),
  ],
)

export const vendorSiteNetworks = pgTable(
  'vendor_site_networks',
  {
    vendorSiteNetworkId: varchar('vendor_site_network_id').primaryKey().notNull(),
    vendorSiteId: varchar('vendor_site_id'),
    type: varchar(),
    haLink: boolean('ha_link').default(false).notNull(),
    name: varchar().notNull(),
    priority: integer(),
    vlanTag: integer('vlan_tag'),
    physport: integer(),
    natStaticPrefix: inet('nat_static_prefix'),
    routedSubnets:
      jsonb('routed_subnets').$type<
        Array<{
          nat?: {
            static_prefix?: string
          }
          next_hop?: string
          prefix?: string
        }>
      >(),
    staticAddressingAddress: inet('static_addressing_address'),
    staticAddressingSecondaryAddress: inet('static_addressing_secondary_address'),
    staticAddressingVirtualAddress: inet('static_addressing_virtual_address'),
    staticAddressingGwAddress: inet('static_addressing_gw_address'),
    dhcpServerPoolEnd: inet('dhcp_server_pool_end'),
    dhcpServerPoolStart: inet('dhcp_server_pool_start'),
    extraData: jsonb('extra_data').$type<InternalCFWan | InternalCFLan | InternalCFACL | MerakiVlanResponse>(),
  },
  (table) => [
    uniqueIndex('uq_static_addressing_address').using(
      'btree',
      table.staticAddressingAddress.asc().nullsLast(),
      table.vendorSiteId.asc().nullsLast().op('text_ops'),
    ),
    foreignKey({
      columns: [table.vendorSiteId],
      foreignColumns: [vendorSites.vendorSiteId],
      name: 'vendor_site_networks_vendor_site_id_vendor_sites_vendor_site_id',
    }).onDelete('cascade'),
  ],
)

export const vendorSites = pgTable(
  'vendor_sites',
  {
    vendorSiteId: varchar('vendor_site_id').primaryKey().notNull(),
    networkProfileId: varchar('network_profile_id'),
    deviceId: varchar('device_id'),
    secondaryDeviceId: varchar('secondary_device_id'),
    extraData: jsonb('extra_data').$type<{
      magicWanSite?: MagicWanSite
      merakiData?: MerakiData
      xiqData?: { siteId?: number; buildingId?: number; floorId?: number }
    }>(),
    type: siteType('site_type'),
    name: varchar(),
    description: varchar(),
  },
  (table) => [
    index('idx_vendor_site_device_id').using('btree', table.deviceId.asc().nullsLast().op('text_ops')),
    index('idx_vendor_site_network_profile_id').using('btree', table.networkProfileId.asc().nullsLast().op('text_ops')),
    foreignKey({
      columns: [table.networkProfileId],
      foreignColumns: [networkProfiles.id],
      name: 'vendor_sites_network_profile_id_network_profiles_id_fk',
    }).onDelete('set null'),
  ],
)

export const clients = pgTable(
  'clients',
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    vendorSiteId: varchar('vendor_site_id').notNull(),
    mac: text('mac').notNull(),
    ip: text('ip'),
    vendor: text('vendor').notNull().default('Unknown'),
    vlan: text('vlan'),
    hostname: text('hostname'),
    seenIn: text('seen_in').array().notNull(),
    lastSeenAt: timestamp('last_seen_at', { withTimezone: true, mode: 'string' }),
    createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp('updated_at', { withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    uniqueIndex('uq_clients_vendor_site_mac').on(table.vendorSiteId, table.mac),
    index('idx_clients_mac').on(table.mac),
    index('idx_clients_vendor_site_vlan').on(table.vendorSiteId, table.vlan),
    index('idx_clients_vendor_site_updated').on(table.vendorSiteId, table.updatedAt),
    foreignKey({
      columns: [table.vendorSiteId],
      foreignColumns: [vendorSites.vendorSiteId],
      name: 'clients_vendor_site_id_vendor_sites_vendor_site_id_fk',
    }).onDelete('cascade'),
  ],
)

export const orgs = pgTable(
  'orgs',
  {
    id: varchar({ length: 255 }).primaryKey().notNull(),
    name: varchar({ length: 255 }).notNull(),
    slug: varchar({ length: 255 }).notNull(),
    upcaseSlug: varchar('upcase_slug', { length: 255 }).notNull(),
    imageUrl: varchar('image_url', { length: 255 }),
    hasImage: boolean('has_image').default(false),
    maxAllowedMemberships: integer('max_allowed_memberships'),
    adminDeleteEnabled: boolean('admin_delete_enabled').default(false),
    membersCount: integer('members_count'),
    mid: varchar({ length: 255 }).default(''),
    publicMetadata: jsonb('public_metadata').$type<OrgPublicMetadata>(),
    privateMetadata: jsonb('private_metadata').$type<OrgPrivateMetadata>(),
    createdBy: varchar('created_by', { length: 255 }),
    orgMetadata: jsonb('org_metadata').$type<OrgMetadata>().default({}),
    networkManaged: boolean('network_managed').default(false),
    extremeEnabled: boolean('extreme_enabled').default(false),
    cradlepointEnabled: boolean('cradlepoint_enabled').default(false),
    magicTransitEnabled: boolean('magic_transit_enabled').default(false),
    merakiEnabled: boolean('meraki_enabled').default(false),
    intuneEnabled: boolean('intune_enabled').default(false),
    extremeData: jsonb('extreme_data').$type<OrgExtremeData>(),
    cfData: jsonb('cf_data').$type<OrgCfData>(),
    merakiData: jsonb('meraki_data').$type<OrgMerakiData>(),
    intuneData: jsonb('intune_data').$type<OrgIntuneData>(),
    cradlepointData: jsonb('cradlepoint_data').$type<OrgCradlepointData>(),
  },
  (table) => [
    index('idx_org_name').using('btree', table.name.asc().nullsLast().op('text_ops')),
    uniqueIndex('uq_org_slug').using('btree', table.slug.asc().nullsLast().op('text_ops')),
    unique('uq_org_id_slug').on(table.id, table.slug),
    unique('orgs_slug_unique').on(table.slug),
    unique('orgs_upcase_slug_unique').on(table.upcaseSlug),
  ],
)

export const functionExecutions = pgTable(
  'function_executions',
  {
    id: varchar({ length: 255 }).$defaultFn(createId).primaryKey().notNull(),
    functionName: varchar('function_name', { length: 255 }).notNull(),
    startedAt: timestamp('started_at', { precision: 6, withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    completedAt: timestamp('completed_at', { precision: 6, withTimezone: true, mode: 'string' }),
    success: boolean().default(false),
    recordsProcessed: text('records_processed'),
    errorMessage: text('error_message'),
    locationId: varchar('location_id').references(() => locations.id, { onDelete: 'set null' }),
    phase: varchar('phase', { length: 255 }),
    workflowId: varchar('workflow_id', { length: 255 }),
  },
  (table) => [
    index('idx_function_executions_location_started').using(
      'btree',
      table.locationId.asc().nullsLast(),
      table.startedAt.asc().nullsLast(),
    ),
  ],
)

export const locations = pgTable(
  'locations',
  {
    id: varchar({ length: 255 }).$defaultFn(createId).primaryKey().notNull(),
    code: varchar({ length: 255 }).notNull(),
    aptosStoreCode: varchar('aptos_store_code', { length: 255 }).notNull(),
    banner: varchar({ length: 24 }).notNull(),
    name: varchar({ length: 255 }).notNull(),
    shortName: varchar('short_name', { length: 255 }),
    status: boolean().default(false).notNull(),
    district: varchar({ length: 255 }),
    region: varchar({ length: 255 }),
    addressCity: varchar('address_city', { length: 255 }),
    addressEmail: varchar('address_email', { length: 255 }),
    addressLine1: varchar('address_line1', { length: 255 }),
    addressLine2: varchar('address_line2', { length: 255 }),
    addressName: varchar('address_name', { length: 255 }),
    addressState: varchar('address_state', { length: 255 }),
    addressZipCode: varchar('address_zip_code', { length: 255 }),
    contactNumber: varchar('contact_number', { length: 50 }),
    contactExtension: varchar('contact_extension', { length: 20 }),
    latitude: real(),
    longitude: real(),
    createdAt: timestamp('created_at', { precision: 6, withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp('updated_at', { precision: 6, withTimezone: true, mode: 'string' }),
    deletedAt: timestamp('deleted_at', { precision: 6, withTimezone: true, mode: 'string' }),
    countryId: integer('country_id'),
    locationTypeLabel: locationTypeEnum('location_type_label'),
    orgId: varchar('org_id', { length: 255 }),
    tags: jsonb('tags').$type<LocationsTags>(),
  },
  (table) => [
    index('idx_code').using('btree', table.code.asc().nullsLast().op('text_ops')),
    index('idx_district').using('btree', table.district.asc().nullsLast().op('text_ops')),
    index('idx_region').using('btree', table.region.asc().nullsLast().op('text_ops')),
    uniqueIndex('uq_aptos_store_code_banner').using(
      'btree',
      table.aptosStoreCode.asc().nullsLast().op('text_ops'),
      table.banner.asc().nullsLast().op('text_ops'),
    ),
    foreignKey({
      columns: [table.orgId],
      foreignColumns: [orgs.id],
      name: 'locations_org_id_orgs_id_fk',
    }).onDelete('cascade'),
    unique('locations_code_unique').on(table.code),
  ],
)

export const networkProfiles = pgTable(
  'network_profiles',
  {
    id: varchar({ length: 255 }).$defaultFn(createId).primaryKey().notNull(),
    edgeVendor: edgeVendor('edge_vendor'),
    switchVendor: switchVendor('switch_vendor'),
    cgVendor: cgVendor('cg_vendor'),
    lifecycleState: lifecycleState('lifecycle_state').default('CONFIGURING').notNull(),
    ipPrimaryGw: inet('ip_primary_gw'),
    ipPrimaryCidr: inet('ip_primary_cidr'),
    ipSchema: inet('ip_schema').array(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    xiqLocId: bigint('xiq_loc_id', { mode: 'number' }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    xiqFloorId: bigint('xiq_floor_id', { mode: 'number' }),
    locationId: varchar('location_id'),
    edgeOverride: boolean('edge_override').default(false).notNull(),
    switchOverride: boolean('switch_override').default(false).notNull(),
    updatedBy: varchar('updated_by').default('system'),
    updatedAt: timestamp('updated_at', { precision: 6, withTimezone: true, mode: 'string' }),
    migrationStatus: networkMigrationStatus('migration_status').default('PENDING'),
    migrationTriggeredBy: varchar('migration_triggered_by'),
    migrationTriggeredAt: timestamp('migration_triggered_at', { precision: 6, withTimezone: true, mode: 'string' }),
    aptosNetwork: inet('aptos_network'),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    xiqBuildingId: bigint('xiq_building_id', { mode: 'number' }),
    ipSecondaryGw: inet('ip_secondary_gw'),
    ipSecondaryCidr: inet('ip_secondary_cidr'),
    ipTertiaryGw: inet('ip_tertiary_gw'),
    ipTertiaryCidr: inet('ip_tertiary_cidr'),
  },
  (table) => [
    index('idx_network_profile_ip_primary_cidr').using('btree', table.ipPrimaryCidr.asc().nullsLast()),
    index('idx_network_profile_ip_schema').using('btree', table.ipSchema.asc().nullsLast()),
    // One store, one range — enforced by the DB, not by convention. A plain index
    // here is what let DTLR0189 and DTLR4600 both hold 172.30.165.0/24 (#1008).
    //
    // Partial + expression on purpose:
    //  - `ip_schema IS NOT NULL AND array_length(ip_schema, 1) = 1` tolerates the 70
    //    profiles with no schema, and matches the real rule — every one of the 449
    //    populated profiles holds exactly one /24.
    //  - a future 2-element profile falls OUT of the index visibly rather than
    //    silently losing protection. Accepted gap, see spec §9.4.
    //  - ip_schema is inet[], so comparison is value-based; no canonicalisation trap.
    uniqueIndex('uq_network_profile_ip_schema_first')
      .using('btree', sql`(${table.ipSchema}[1])`)
      .where(sql`${table.ipSchema} IS NOT NULL AND array_length(${table.ipSchema}, 1) = 1`),
    index('idx_network_profile_location_id').using('btree', table.locationId.asc().nullsLast().op('text_ops')),
    uniqueIndex('uq_network_profile_location_id').using('btree', table.locationId.asc().nullsLast().op('text_ops')),
    foreignKey({
      columns: [table.locationId],
      foreignColumns: [locations.id],
      name: 'network_profiles_location_id_locations_id_fk',
    }).onDelete('cascade'),
  ],
)

export const reservations = pgTable(
  'reservations',
  {
    id: varchar({ length: 255 }).$defaultFn(createId).primaryKey().notNull(),
    siteId: varchar('site_id'),
    mac: varchar(),
    ip: varchar(),
    type: varchar(),
    dnsRecordId: varchar('dns_record_id'),
    name: varchar(),
    updatedBy: varchar('updated_by').default('system'),
    updatedAt: timestamp('updated_at', { precision: 6, withTimezone: true, mode: 'string' }),
    status: varchar().default('CREATING'),
    networkId: varchar('network_id'),
    networkProfileId: varchar('network_profile_id'),
  },
  (table) => [
    foreignKey({
      columns: [table.siteId],
      foreignColumns: [vendorSites.vendorSiteId],
      name: 'reservations_site_id_vendor_sites_vendor_site_id_fk',
    }).onDelete('set null'),
    foreignKey({
      columns: [table.networkId],
      foreignColumns: [vendorSiteNetworks.vendorSiteNetworkId],
      name: 'reservations_network_id_vendor_site_networks_vendor_site_networ',
    }).onDelete('set null'),
    index('idx_reservation_network_profile_id').using('btree', table.networkProfileId.asc().nullsLast().op('text_ops')),
    index('idx_reservation_site_id').using('btree', table.siteId.asc().nullsLast().op('text_ops')),
    foreignKey({
      columns: [table.networkProfileId],
      foreignColumns: [networkProfiles.id],
      name: 'reservations_network_profile_id_network_profiles_id_fk',
    }).onDelete('set null'),
  ],
)

export const paymentsProfiles = pgTable(
  'payments_profiles',
  {
    locationId: varchar('location_id').notNull(),
    adyenMerchantId: varchar('adyen_merchant_id', { length: 255 }),
    adyenId: varchar('adyen_id', { length: 255 }).notNull(),
    adyenReference: varchar('adyen_reference', { length: 255 }),
    adyenStatus: varchar('adyen_status', { length: 255 }),
    adyenDescription: varchar('adyen_description', { length: 255 }),
    paymentMethods: varchar('payment_methods', { length: 255 }).array(),
    createdAt: timestamp('created_at', { precision: 6, withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp('updated_at', { precision: 6, withTimezone: true, mode: 'string' }),
  },
  (table) => [
    index('idx_payments_profiles_adyen_reference').using(
      'btree',
      table.adyenReference.asc().nullsLast().op('text_ops'),
    ),
    foreignKey({
      columns: [table.locationId],
      foreignColumns: [locations.id],
      name: 'payments_profiles_location_id_locations_id_fk',
    }).onDelete('cascade'),
    primaryKey({ columns: [table.locationId, table.adyenId], name: 'payments_profiles_location_id_adyen_id_pk' }),
    unique('payments_profiles_adyen_id_unique').on(table.adyenId),
    unique('payments_profiles_adyen_reference_unique').on(table.adyenReference),
  ],
)

export const nodes = pgTable(
  'nodes',
  {
    kind: nodeKindEnum('kind').notNull(),
    lastSeen: timestamp('last_seen', { precision: 6, withTimezone: true, mode: 'string' }),
    networkProfileId: varchar('network_profile_id'),
    mac: macaddr(),
    model: varchar().notNull(),
    name: varchar().notNull(),
    serial: varchar().notNull(),
    status: boolean().notNull(),
    orgId: varchar('org_id'),
    vendorSiteId: varchar('vendor_site_id'),
    externalId: varchar('external_id'),
    createdAt: timestamp('created_at', { precision: 6, withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp('updated_at', { precision: 6, withTimezone: true, mode: 'string' }),
    manufacturer: varchar().notNull(),
    extraData: jsonb('extra_data').$type<GenericJsonb>(),
    syncPlatform: varchar('sync_platform'),
  },
  (table) => [
    index('idx_node_model').using('btree', table.model.asc().nullsLast().op('text_ops')),
    index('idx_node_name').using('btree', table.name.asc().nullsLast().op('text_ops')),
    uniqueIndex('uq_node_serial').using('btree', table.serial.asc().nullsLast().op('text_ops')),
    foreignKey({
      columns: [table.orgId],
      foreignColumns: [orgs.id],
      name: 'nodes_org_id_orgs_id_fk',
    }).onDelete('set null'),
    foreignKey({
      columns: [table.networkProfileId],
      foreignColumns: [networkProfiles.id],
      name: 'nodes_network_profile_id_network_profiles_id_fk',
    }).onDelete('set null'),
    foreignKey({
      columns: [table.vendorSiteId],
      foreignColumns: [vendorSites.vendorSiteId],
      name: 'nodes_vendor_site_id_vendor_sites_vendor_site_id_fk',
    }).onDelete('set null'),
    primaryKey({
      columns: [table.model, table.serial, table.manufacturer],
      name: 'nodes_serial_manufacturer_model_pk',
    }),
    unique('nodes_serial_unique').on(table.serial),
  ],
)
export const wanData = pgTable(
  'wan_data',
  {
    id: varchar({ length: 255 }).$defaultFn(createId).primaryKey().notNull(),
    type: wanTypeEnum('type').notNull(),
    assignmentType: wanAssignmentTypeEnum('assignment_type').notNull(),
    assignedCidr: inet('assigned_cidr'),
    assignedIp: inet('assigned_ip'),
    subnet: inet('subnet'),
    gw: inet('gw'),
    dhcp: boolean().default(false).notNull(),
    rangeStart: inet('range_start'),
    rangeEnd: inet('range_end'),
    status: boolean().default(true).notNull(),
    rxSpeed: varchar('rx_speed', { length: 50 }),
    txSpeed: varchar('tx_speed', { length: 50 }),
    networkProfileId: varchar('network_profile_id'),
    createdAt: timestamp('created_at', { precision: 6, withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    modifiedAt: timestamp('modified_at', { precision: 6, withTimezone: true, mode: 'string' }),
    deletedAt: timestamp('deleted_at', { precision: 6, withTimezone: true, mode: 'string' }),
    extraData: jsonb('extra_data').$type<GenericJsonb>(),
    carrier: varchar('carrier', { length: 255 }).notNull(),
    source: wanSourceEnum('source').notNull(),
  },
  (table) => [
    index('idx_wan_data_type').using('btree', table.type.asc().nullsLast()),
    index('idx_wan_data_assignment_type').using('btree', table.assignmentType.asc().nullsLast()),
    index('idx_wan_data_carrier').using('btree', table.carrier.asc().nullsLast().op('text_ops')),
    index('idx_wan_data_source').using('btree', table.source.asc().nullsLast()),
    index('idx_wan_data_network_profile_id').using('btree', table.networkProfileId.asc().nullsLast().op('text_ops')),
    foreignKey({
      columns: [table.networkProfileId],
      foreignColumns: [networkProfiles.id],
      name: 'wan_data_network_profile_id_network_profiles_id_fk',
    }).onDelete('set null'),
    unique('wan_data_network_profile_id_type_unique').on(table.networkProfileId, table.type),
  ],
)

export const locationStatusStats = pgView('location_status_stats', {
  banner: varchar({ length: 24 }),
  status: boolean(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  count: bigint({ mode: 'number' }),
}).as(sql`SELECT banner, status, count(*) AS count FROM locations GROUP BY banner, status`)

export const terminalFirmwareStats = pgView('terminal_firmware_stats', {
  merchantId: varchar('merchant_id', { length: 255 }),
  firmwareVersion: varchar('firmware_version', { length: 255 }),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  count: bigint({ mode: 'number' }),
}).as(
  sql`SELECT merchant_id, firmware_version, count(*) AS count FROM terminals GROUP BY merchant_id, firmware_version`,
)

export const terminalModelStats = pgView('terminal_model_stats', {
  merchantId: varchar('merchant_id', { length: 255 }),
  model: varchar({ length: 255 }),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  count: bigint({ mode: 'number' }),
}).as(sql`SELECT merchant_id, model, count(*) AS count FROM terminals GROUP BY merchant_id, model`)

export const terminalStatusStats = pgView('terminal_status_stats', {
  merchantId: varchar('merchant_id', { length: 255 }),
  status: varchar({ length: 255 }),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  count: bigint({ mode: 'number' }),
}).as(sql`SELECT merchant_id, status, count(*) AS count FROM terminals GROUP BY merchant_id, status`)

export const appRoles = pgTable('app_roles', {
  id: text('id').primaryKey().notNull(), // caller must supply slug, e.g. 'org:admin'
  name: text('name').notNull(),
  description: text('description'),
  createdAt: timestamp('created_at', { precision: 6, withTimezone: true, mode: 'string' })
    .default(sql`CURRENT_TIMESTAMP`)
    .notNull(),
  updatedAt: timestamp('updated_at', { precision: 6, withTimezone: true, mode: 'string' })
    .default(sql`CURRENT_TIMESTAMP`)
    .notNull(),
})

export const appPermissions = pgTable('app_permissions', {
  id: text('id').primaryKey().notNull(), // caller must supply slug, e.g. 'read:locations'
  name: text('name').notNull(),
  description: text('description'),
  groupTag: text('group_tag').notNull(), // matches OpenAPI tag, e.g. 'Locations'
  createdAt: timestamp('created_at', { precision: 6, withTimezone: true, mode: 'string' })
    .default(sql`CURRENT_TIMESTAMP`)
    .notNull(),
})

export const appRolePermissions = pgTable(
  'app_role_permissions',
  {
    roleId: text('role_id')
      .notNull()
      .references(() => appRoles.id, { onDelete: 'cascade' }),
    permissionId: text('permission_id')
      .notNull()
      .references(() => appPermissions.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at', { precision: 6, withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (t) => [primaryKey({ columns: [t.roleId, t.permissionId] }), index('idx_app_role_permissions_role_id').on(t.roleId)],
)

export type SelectAppRole = typeof appRoles.$inferSelect
export type InsertAppRole = typeof appRoles.$inferInsert
export type SelectAppPermission = typeof appPermissions.$inferSelect
export type InsertAppPermission = typeof appPermissions.$inferInsert
export type SelectAppRolePermission = typeof appRolePermissions.$inferSelect
export type InsertAppRolePermission = typeof appRolePermissions.$inferInsert

export const driftAlerts = pgTable(
  'drift_alerts',
  {
    id: varchar({ length: 255 }).$defaultFn(createId).primaryKey().notNull(),
    vendor: varchar({ length: 50 }).notNull(),
    alertType: varchar('alert_type', { length: 100 }).notNull(),
    severity: varchar({ length: 20 }).notNull(),
    locationId: varchar('location_id', { length: 255 }),
    entityId: varchar('entity_id', { length: 255 }).notNull(),
    entityKind: varchar('entity_kind', { length: 50 }).notNull(),
    message: text().notNull(),
    details: jsonb('details').$type<Record<string, unknown>>(),
    detectedAt: timestamp('detected_at', { precision: 6, withTimezone: true, mode: 'string' }).notNull(),
    runId: varchar('run_id', { length: 255 }),
  },
  (table) => [
    index('idx_drift_location_id').on(table.locationId),
    index('idx_drift_vendor').on(table.vendor),
    index('idx_drift_alert_type').on(table.alertType),
    index('idx_drift_severity').on(table.severity),
    foreignKey({
      columns: [table.locationId],
      foreignColumns: [locations.id],
      name: 'drift_alerts_location_id_locations_id_fk',
    }),
  ],
)

export type InsertDriftAlert = typeof driftAlerts.$inferInsert
export type SelectDriftAlert = typeof driftAlerts.$inferSelect

export const apiTokens = pgTable(
  'api_tokens',
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    name: text('name').notNull(),
    tokenHash: text('token_hash').notNull(), // sha256 hex of raw token
    prefixDisplay: text('prefix_display').notNull(), // e.g. "ody_test_a3b4cd"
    scopes: text('scopes').array().notNull(), // permission strings (read:network, etc.)
    createdBy: varchar('created_by', { length: 255 }).notNull(), // Clerk userId
    createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    expiresAt: timestamp('expires_at', { withTimezone: true, mode: 'string' }),
    lastSeenAt: timestamp('last_seen_at', { withTimezone: true, mode: 'string' }),
    revokedAt: timestamp('revoked_at', { withTimezone: true, mode: 'string' }),
  },
  (table) => [
    uniqueIndex('uq_api_tokens_hash').on(table.tokenHash),
    index('idx_api_tokens_created_by').on(table.createdBy),
  ],
)

export type InsertApiToken = typeof apiTokens.$inferInsert
export type SelectApiToken = typeof apiTokens.$inferSelect

// ── Network Status Polling ──────────────────────────────────────────

export const deviceStatusPolls = pgTable(
  'device_status_polls',
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    nodeSerial: varchar('node_serial').notNull(),
    nodeManufacturer: varchar('node_manufacturer', { length: 64 }).notNull(),
    polledStatus: varchar('polled_status').notNull(),
    rawData: jsonb('raw_data').$type<Record<string, unknown>>(),
    pollRunId: varchar('poll_run_id').notNull(),
    polledAt: timestamp('polled_at', { withTimezone: true, mode: 'string' }).notNull(),
  },
  (table) => [
    index('idx_device_status_polls_serial_polled').on(table.nodeSerial, table.polledAt.desc()),
    foreignKey({
      columns: [table.nodeSerial],
      foreignColumns: [nodes.serial],
      name: 'device_status_polls_node_serial_fk',
    }),
  ],
)

export type InsertDeviceStatusPoll = typeof deviceStatusPolls.$inferInsert
export type SelectDeviceStatusPoll = typeof deviceStatusPolls.$inferSelect

// ── Cellular Usage ──────────────────────────────────────────

export const cellularUsageRuns = pgTable(
  'cellular_usage_runs',
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    orgId: varchar('org_id').notNull(),
    periodStart: timestamp('period_start', { withTimezone: true, mode: 'string' }).notNull(),
    periodEnd: timestamp('period_end', { withTimezone: true, mode: 'string' }).notNull(),
    trigger: varchar('trigger', { length: 16 }).notNull(),
    startedAt: timestamp('started_at', { withTimezone: true, mode: 'string' }).notNull(),
    completedAt: timestamp('completed_at', { withTimezone: true, mode: 'string' }).notNull(),
    complete: boolean('complete').notNull(),
    partial: boolean('partial').notNull(),
    expectedStores: integer('expected_stores').notNull(),
    reportedGateways: integer('reported_gateways').notNull(),
    missing: jsonb('missing').$type<{ store: string; serial?: string; reason: string }[]>().notNull(),
  },
  (table) => [index('idx_cellular_usage_runs_period_completed').on(table.periodEnd, table.completedAt.desc())],
)

export type InsertCellularUsageRun = typeof cellularUsageRuns.$inferInsert
export type SelectCellularUsageRun = typeof cellularUsageRuns.$inferSelect

export const cellularUsageSnapshots = pgTable(
  'cellular_usage_snapshots',
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    runId: uuid('run_id').notNull(),
    orgId: varchar('org_id').notNull(),
    storeCode: varchar('store_code', { length: 16 }).notNull(),
    serial: varchar('serial').notNull(),
    model: varchar('model', { length: 32 }).notNull(),
    periodStart: timestamp('period_start', { withTimezone: true, mode: 'string' }).notNull(),
    periodEnd: timestamp('period_end', { withTimezone: true, mode: 'string' }).notNull(),
    totalBytes: bigint('total_bytes', { mode: 'number' }).notNull(),
    sim1Bytes: bigint('sim1_bytes', { mode: 'number' }),
    sim2Bytes: bigint('sim2_bytes', { mode: 'number' }),
    sim3Bytes: bigint('sim3_bytes', { mode: 'number' }),
    firstIntervalAt: timestamp('first_interval_at', { withTimezone: true, mode: 'string' }).notNull(),
    lastIntervalAt: timestamp('last_interval_at', { withTimezone: true, mode: 'string' }).notNull(),
    partial: boolean('partial').notNull(),
    capturedAt: timestamp('captured_at', { withTimezone: true, mode: 'string' }).notNull(),
  },
  (table) => [
    uniqueIndex('uq_cellular_usage_snapshots_serial_period').on(table.serial, table.periodStart, table.periodEnd),
    index('idx_cellular_usage_snapshots_period_store').on(table.periodEnd, table.storeCode),
    foreignKey({
      columns: [table.runId],
      foreignColumns: [cellularUsageRuns.id],
      name: 'cellular_usage_snapshots_run_id_fk',
    }),
  ],
)

export type InsertCellularUsageSnapshot = typeof cellularUsageSnapshots.$inferInsert
export type SelectCellularUsageSnapshot = typeof cellularUsageSnapshots.$inferSelect

export const deviceStatusCurrent = pgTable(
  'device_status_current',
  {
    nodeSerial: varchar('node_serial').primaryKey().notNull(),
    nodeManufacturer: varchar('node_manufacturer', { length: 64 }).notNull(),
    nodeKind: nodeKindEnum('node_kind').notNull(),
    vendorStatus: varchar('vendor_status').notNull(),
    decision: varchar('decision').notNull(),
    decisionReason: varchar('decision_reason'),
    lastPolledAt: timestamp('last_polled_at', { withTimezone: true, mode: 'string' }).notNull(),
    lastChangeAt: timestamp('last_change_at', { withTimezone: true, mode: 'string' }),
    networkProfileId: varchar('network_profile_id'),
    orgId: varchar('org_id'),
  },
  (table) => [
    index('idx_device_status_current_np').on(table.networkProfileId),
    index('idx_device_status_current_org').on(table.orgId),
    foreignKey({
      columns: [table.nodeSerial],
      foreignColumns: [nodes.serial],
      name: 'device_status_current_node_serial_fk',
    }),
  ],
)

export type InsertDeviceStatusCurrent = typeof deviceStatusCurrent.$inferInsert
export type SelectDeviceStatusCurrent = typeof deviceStatusCurrent.$inferSelect

export const tunnelStatusCurrent = pgTable(
  'tunnel_status_current',
  {
    tunnelName: varchar('tunnel_name').primaryKey().notNull(),
    storeCode: varchar('store_code'),
    tunnelType: varchar('tunnel_type').notNull(),
    vendor: varchar('vendor').notNull(),
    vendorStatus: varchar('vendor_status').notNull(),
    decision: varchar('decision').notNull(),
    networkId: varchar('network_id'),
    orgId: varchar('org_id'),
    lastPolledAt: timestamp('last_polled_at', { withTimezone: true, mode: 'string' }).notNull(),
    lastChangeAt: timestamp('last_change_at', { withTimezone: true, mode: 'string' }),
  },
  (table) => [
    index('idx_tunnel_status_current_store').on(table.storeCode),
    index('idx_tunnel_status_current_org').on(table.orgId),
  ],
)

export type InsertTunnelStatusCurrent = typeof tunnelStatusCurrent.$inferInsert
export type SelectTunnelStatusCurrent = typeof tunnelStatusCurrent.$inferSelect

export const tunnelStatusPolls = pgTable(
  'tunnel_status_polls',
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    tunnelName: varchar('tunnel_name').notNull(),
    storeCode: varchar('store_code'),
    vendor: varchar('vendor').notNull(),
    vendorStatus: varchar('vendor_status').notNull(),
    pollRunId: varchar('poll_run_id').notNull(),
    polledAt: timestamp('polled_at', { withTimezone: true, mode: 'string' }).notNull(),
  },
  (table) => [index('idx_tunnel_status_polls_name_polled').on(table.tunnelName, table.polledAt.desc())],
)

export type InsertTunnelStatusPoll = typeof tunnelStatusPolls.$inferInsert
export type SelectTunnelStatusPoll = typeof tunnelStatusPolls.$inferSelect

export const backboneStatusCurrent = pgTable(
  'backbone_status_current',
  {
    tunnelName: varchar('tunnel_name').primaryKey().notNull(),
    vendor: varchar('vendor').notNull(),
    vendorStatus: varchar('vendor_status').notNull(),
    decision: varchar('decision').notNull(),
    orgId: varchar('org_id'),
    lastPolledAt: timestamp('last_polled_at', { withTimezone: true, mode: 'string' }).notNull(),
    lastChangeAt: timestamp('last_change_at', { withTimezone: true, mode: 'string' }),
  },
  (table) => [index('idx_backbone_status_current_org').on(table.orgId)],
)

export type InsertBackboneStatusCurrent = typeof backboneStatusCurrent.$inferInsert
export type SelectBackboneStatusCurrent = typeof backboneStatusCurrent.$inferSelect

export const wanInterfaceStatusCurrent = pgTable(
  'wan_interface_status_current',
  {
    // stable id: `${nodeSerial}:${interfaceName}`
    interfaceKey: varchar('interface_key').primaryKey().notNull(),
    nodeSerial: varchar('node_serial'),
    interfaceName: varchar('interface_name').notNull(),
    wanType: varchar('wan_type'), // PRIMARY/SECONDARY/TERTIARY when known, else null
    vendor: varchar('vendor').notNull(), // Meraki | Cloudflare | Cradlepoint
    vendorStatus: varchar('vendor_status').notNull(),
    decision: varchar('decision').notNull(), // UP | DOWN | DEGRADED
    decisionReason: varchar('decision_reason'),
    storeCode: varchar('store_code'),
    networkProfileId: varchar('network_profile_id'),
    orgId: varchar('org_id'),
    lastPolledAt: timestamp('last_polled_at', { withTimezone: true, mode: 'string' }).notNull(),
    lastChangeAt: timestamp('last_change_at', { withTimezone: true, mode: 'string' }),
  },
  (table) => [
    index('idx_wan_iface_status_current_store').on(table.storeCode),
    index('idx_wan_iface_status_current_np').on(table.networkProfileId),
    index('idx_wan_iface_status_current_org').on(table.orgId),
  ],
)

export type InsertWanInterfaceStatusCurrent = typeof wanInterfaceStatusCurrent.$inferInsert
export type SelectWanInterfaceStatusCurrent = typeof wanInterfaceStatusCurrent.$inferSelect

export const printerHostOverrides = pgTable(
  'printer_host_overrides',
  {
    id: varchar({ length: 255 }).$defaultFn(createId).primaryKey().notNull(),
    banner: varchar('banner', { length: 255 }).notNull(),
    locationCode: varchar('location_code', { length: 255 }).notNull(),
    workstationId: varchar('workstation_id', { length: 255 }).notNull(),
    printerRole: varchar('printer_role', { length: 255 }).notNull().default('network-receipt-printer'),
    value: varchar('value', { length: 255 }).notNull(),
    printerModel: varchar('printer_model', { length: 255 }),
    reservationId: varchar('reservation_id', { length: 255 }),
    overrideUntil: timestamp('override_until', { withTimezone: true, mode: 'string' }).notNull(),
    setBy: varchar('set_by', { length: 255 }).notNull(),
    createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp('updated_at', { withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    uniqueIndex('printer_host_overrides_device_role_uniq').on(
      table.banner,
      table.locationCode,
      table.workstationId,
      table.printerRole,
    ),
  ],
)

export type InsertPrinterHostOverride = typeof printerHostOverrides.$inferInsert
export type SelectPrinterHostOverride = typeof printerHostOverrides.$inferSelect

export const infraServiceRequests = pgTable(
  'infra_service_requests',
  {
    id: varchar({ length: 255 }).$defaultFn(createId).primaryKey().notNull(),
    instanceName: varchar('instance_name', { length: 255 }).notNull(),
    serviceType: varchar('service_type', { length: 64 }).notNull(),
    namespace: varchar('namespace', { length: 255 }).notNull(),
    team: varchar('team', { length: 255 }).notNull(),
    ttlExpiry: timestamp('ttl_expiry', { withTimezone: true, mode: 'string' }),
    status: varchar('status', { length: 64 }).notNull().default('pending'),
    credentialsSecretName: varchar('credentials_secret_name', { length: 255 }),
    manifestPrUrl: text('manifest_pr_url'),
    requestedBy: varchar('requested_by', { length: 255 }).notNull(),
    createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp('updated_at', { withTimezone: true, mode: 'string' }),
    deletedAt: timestamp('deleted_at', { withTimezone: true, mode: 'string' }),
  },
  (table) => [
    index('idx_infra_team').on(table.team),
    index('idx_infra_status').on(table.status),
    index('idx_infra_namespace').on(table.namespace),
    index('idx_infra_requested_by').on(table.requestedBy),
  ],
)

export type InsertInfraServiceRequest = typeof infraServiceRequests.$inferInsert
export type SelectInfraServiceRequest = typeof infraServiceRequests.$inferSelect

// ─────────────────────────────────────────────────────────────────────────────
// NetOps ticketing (#1153) — see prompts/netops-ticketing/spec.md
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Where a ticket came IN from. Phase 3 adds one label per intake surface.
 *
 * `escalation` predates them and is REUSED by the sync-app-ui surface rather than
 * adding a `ui` label — `idx_tickets_location` and `idx_tickets_org` were created for
 * exactly that (spec.md:441-442), and a Postgres enum label can never be dropped.
 *
 * `email` is deliberately ABSENT. Inbound email was evaluated and dropped: Cloudflare
 * Email Routing "cannot be used with external mail servers", so enabling it on a domain
 * whose apex MX points at Microsoft 365 breaks all company mail. A label here would
 * advertise a path that does not exist.
 *
 * ! Adding a label is safe in the migrator's single transaction; WRITING one in that
 * same transaction is not (#1231, `unsafe use of new value`). Application code runs
 * after the migration commits, so that holds by construction — but a data-backfill
 * migration touching these labels must ship as its own, separately applied migration.
 */
export const ticketSource = pgEnum('ticket_source', ['jira', 'manual', 'escalation', 'slack', 'teams', 'cli'])

export const ticketStatus = pgEnum('ticket_status', [
  'open',
  'acknowledged',
  'in_progress',
  'waiting',
  'resolved',
  'cancelled',
])

export const ticketEventKind = pgEnum('ticket_event_kind', [
  'comment',
  'status_change',
  'slack_action',
  'jira_post',
  'error',
])

// 'skipped' is the cutover state (#1224): tickets that reached a terminal status before the
// Slack integration existed must never post. Marking them 'posted' would be wrong and
// unrecoverable — there is no CHECK tying slack_post_state to slack_message_ts, so a
// 'posted' row with a NULL slack_message_ts is legal and permanently indistinguishable from
// the exact write-failure bug this enum was added to detect (spec.md, "Slack posting is a
// separate idempotency problem").
export const slackPostState = pgEnum('slack_post_state', ['pending', 'posted', 'failed', 'skipped'])

export const tickets = pgTable(
  'tickets',
  {
    id: varchar({ length: 255 }).$defaultFn(createId).primaryKey().notNull(),
    number: integer().generatedByDefaultAsIdentity(),

    source: ticketSource().notNull(),
    status: ticketStatus().notNull().default('open'),
    team: varchar({ length: 64 }).notNull().default('netops'),
    priority: varchar({ length: 32 }),

    externalId: varchar('external_id', { length: 64 }),
    externalUrl: varchar('external_url', { length: 512 }),
    /**
     * A link back to where the ticket came from — a Slack permalink, a Teams
     * `linkToMessage`, whatever a future surface offers.
     *
     * SEPARATE from `external_url` on purpose. That column means "the Jira issue" and
     * `ck_tickets_external_url` pins it to dtlr.atlassian.net; widening it would have
     * made one column mean two things and removed a deliberate guard. Four adapters
     * write this one, which is why it carries a guard of its own.
     */
    sourceUrl: varchar('source_url', { length: 512 }),
    summary: varchar({ length: 512 }).notNull(),

    reporterRaw: varchar('reporter_raw', { length: 512 }),
    reporterStoreCode: varchar('reporter_store_code', { length: 4 }),

    locationId: varchar('location_id', { length: 255 }),
    orgId: varchar('org_id', { length: 255 }),
    assigneeEmail: varchar('assignee_email', { length: 255 }),

    slackChannelId: varchar('slack_channel_id', { length: 32 }),
    slackMessageTs: varchar('slack_message_ts', { length: 32 }),
    slackPostState: slackPostState('slack_post_state').notNull().default('pending'),
    slackPostAttempts: integer('slack_post_attempts').notNull().default(0),
    // The ticket status the currently-posted Slack message RENDERS — not the ticket's own
    // status. NULL means nothing has been posted yet. Phase 2B edits the Slack message when
    // a ticket resolves or cancels, and nothing else records what was last posted, so there
    // is no other way to detect a transition. The cheap alternative — re-render every posted
    // row each sweep — would issue up to one chat.update per posted ticket per 15-minute
    // tick against a Tier-3 Slack rate limit. `status IS DISTINCT FROM slack_posted_status`
    // makes the update precise: it fires once per real transition and never otherwise.
    slackPostedStatus: ticketStatus('slack_posted_status'),

    rawPayload: jsonb('raw_payload').$type<unknown>(),
    rawPayloadExpiresAt: timestamp('raw_payload_expires_at', {
      withTimezone: true,
      mode: 'string',
    }),

    createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp('updated_at', { withTimezone: true, mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`),
    resolvedAt: timestamp('resolved_at', { withTimezone: true, mode: 'string' }),
    deletedAt: timestamp('deleted_at', { withTimezone: true, mode: 'string' }),
  },
  (table) => [
    foreignKey({
      columns: [table.locationId],
      foreignColumns: [locations.id],
      name: 'tickets_location_id_locations_id_fk',
    }).onDelete('set null'),
    foreignKey({
      columns: [table.orgId],
      foreignColumns: [orgs.id],
      name: 'tickets_org_id_orgs_id_fk',
    }).onDelete('set null'),

    // The dedup constraint. Any change to this predicate MUST be mirrored in every
    // `targetWhere` of every onConflictDoUpdate against this table — Postgres can only
    // use a partial index as an ON CONFLICT arbiter when the inference clause provably
    // implies the predicate, and a mismatch fails at RUNTIME with 42P10, not at typecheck.
    uniqueIndex('uq_tickets_open_external')
      .on(table.team, table.source, table.externalId)
      .where(sql`${table.status} NOT IN ('resolved','cancelled') AND ${table.externalId} IS NOT NULL`),
    uniqueIndex('uq_tickets_number').on(table.number),

    index('idx_tickets_location').on(table.locationId),
    index('idx_tickets_org').on(table.orgId),

    check('ck_tickets_resolved_at', sql`(${table.status} = 'resolved') = (${table.resolvedAt} IS NOT NULL)`),
    check('ck_tickets_jira_reporter', sql`${table.source} <> 'jira' OR ${table.reporterRaw} IS NOT NULL`),
    check(
      'ck_tickets_assignee_lower',
      sql`${table.assigneeEmail} IS NULL OR ${table.assigneeEmail} = lower(${table.assigneeEmail})`,
    ),
    check(
      'ck_tickets_external_url',
      sql`${table.externalUrl} IS NULL OR ${table.externalUrl} LIKE 'https://dtlr.atlassian.net/%'`,
    ),
    // Deliberately loose — origins differ per surface and pinning the known hosts would
    // need a migration for every new one, which is the trap external_url is in and the
    // reason this column exists. It catches the failure four adapters actually invite:
    // a permalink field filled with a bare message id or a Slack ts.
    check('ck_tickets_source_url', sql`${table.sourceUrl} IS NULL OR ${table.sourceUrl} LIKE 'https://%'`),
  ],
)

export type InsertTicket = typeof tickets.$inferInsert
export type SelectTicket = typeof tickets.$inferSelect

export const ticketEvents = pgTable(
  'ticket_events',
  {
    id: varchar({ length: 255 }).$defaultFn(createId).primaryKey().notNull(),
    seq: bigserial({ mode: 'number' }).notNull(),
    ticketId: varchar('ticket_id', { length: 255 }).notNull(),

    kind: ticketEventKind().notNull(),
    body: text(),
    isInternal: boolean('is_internal').notNull().default(true),

    fromStatus: ticketStatus('from_status'),
    toStatus: ticketStatus('to_status'),

    actorEmail: varchar('actor_email', { length: 255 }),
    actorKind: varchar('actor_kind', { length: 16 }).notNull().default('human'),

    externalEventId: varchar('external_event_id', { length: 128 }),
    jiraPostedAt: timestamp('jira_posted_at', { withTimezone: true, mode: 'string' }),

    payload: jsonb().$type<unknown>(),
    // clock_timestamp(), not CURRENT_TIMESTAMP: the latter is transaction-START time, so
    // two events written in one db.transaction would tie and ORDER BY created_at would be
    // non-deterministic. Timeline queries order by (created_at, seq).
    createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' })
      .default(sql`clock_timestamp()`)
      .notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.ticketId],
      foreignColumns: [tickets.id],
      name: 'ticket_events_ticket_id_tickets_id_fk',
    }).onDelete('cascade'),

    uniqueIndex('uq_ticket_events_external')
      .on(table.kind, table.externalEventId)
      .where(sql`${table.externalEventId} IS NOT NULL`),

    index('idx_ticket_events_ticket').on(table.ticketId, table.createdAt, table.seq),

    check('ck_events_internal', sql`${table.isInternal} OR ${table.kind} = 'comment'`),
    check('ck_events_comment_body', sql`${table.kind} <> 'comment' OR ${table.body} IS NOT NULL`),
    check('ck_events_status_change', sql`${table.kind} <> 'status_change' OR ${table.toStatus} IS NOT NULL`),
    check(
      'ck_events_actor_lower',
      sql`${table.actorEmail} IS NULL OR ${table.actorEmail} = lower(${table.actorEmail})`,
    ),
  ],
)

export type InsertTicketEvent = typeof ticketEvents.$inferInsert
export type SelectTicketEvent = typeof ticketEvents.$inferSelect

/**
 * Outcome of one sweep run. `partial` means the sweep completed but individual
 * issues failed — distinct from `error`, where the run itself threw.
 */
export const sweepOutcome = pgEnum('sweep_outcome', ['ok', 'partial', 'error'])

/**
 * One row per ticket-worker sweep, success or failure.
 *
 * WHY THIS TABLE EXISTS. At ~1.6 tickets/week, "no new tickets for ten days" is a
 * normal week, so absence of output can never be the alarm — liveness has to be
 * proven positively. Sentry cron monitors cover "did it run at all"; this table
 * covers "what did it do", which is the part a missed check-in cannot answer and
 * which every post-incident question needs.
 *
 * Append-only. Nothing updates a row after `finished_at` is written.
 */
export const ticketSweepRuns = pgTable(
  'ticket_sweep_runs',
  {
    id: varchar({ length: 255 }).$defaultFn(createId).primaryKey().notNull(),

    startedAt: timestamp('started_at', { withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    finishedAt: timestamp('finished_at', { withTimezone: true, mode: 'string' }),
    outcome: sweepOutcome().notNull(),

    pagesFetched: integer('pages_fetched').notNull().default(0),
    jiraResultCount: integer('jira_result_count').notNull().default(0),
    rowsCreated: integer('rows_created').notNull().default(0),
    rowsUpdated: integer('rows_updated').notNull().default(0),
    rowsClosed: integer('rows_closed').notNull().default(0),
    payloadsPurged: integer('payloads_purged').notNull().default(0),
    errors: integer().notNull().default(0),

    durationMs: integer('duration_ms'),
    /** True when wall time approached the Workers limit — a sweep that is about to
     * start timing out looks identical to a healthy one until it does. */
    nearTimeLimit: boolean('near_time_limit').notNull().default(false),

    /** Present only when outcome = 'error'. */
    errorMessage: text('error_message'),
  },
  (table) => [
    index('idx_sweep_runs_started').on(table.startedAt),

    // finished_at and duration_ms are written together or not at all; a row with one
    // and not the other is a bug in the writer, not a state worth representing.
    check('ck_sweep_runs_finished', sql`(${table.finishedAt} IS NULL) = (${table.durationMs} IS NULL)`),
    check('ck_sweep_runs_error_message', sql`${table.outcome} = 'error' OR ${table.errorMessage} IS NULL`),
  ],
)

export type InsertTicketSweepRun = typeof ticketSweepRuns.$inferInsert
export type SelectTicketSweepRun = typeof ticketSweepRuns.$inferSelect

/** NetOps report DM ledger (#1380). text + CHECK, not pgEnum: see spec §6.3 (ADD VALUE transaction trap). */
export const reportDeliveries = pgTable(
  'report_deliveries',
  {
    id: varchar({ length: 255 }).$defaultFn(createId).primaryKey().notNull(),
    kind: text().notNull(),
    subjectAccountId: varchar('subject_account_id', { length: 128 }),
    recipientAccountId: varchar('recipient_account_id', { length: 128 }).notNull(),
    localDate: date('local_date', { mode: 'string' }).notNull(),
    preview: boolean().notNull().default(false),
    status: text().notNull().default('pending'),
    attempts: integer().notNull().default(0),
    slackUserId: varchar('slack_user_id', { length: 32 }),
    slackChannel: varchar('slack_channel', { length: 32 }),
    slackTs: varchar('slack_ts', { length: 32 }),
    errorCode: varchar('error_code', { length: 64 }),
    claimedAt: timestamp('claimed_at', { withTimezone: true, mode: 'string' }),
    createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp('updated_at', { withTimezone: true, mode: 'string' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (t) => [
    uniqueIndex('uq_report_deliveries').on(t.kind, t.recipientAccountId, t.localDate).where(sql`${t.preview} = false`),
    check('ck_report_deliveries_kind', sql`${t.kind} IN ('management','user')`),
    check(
      'ck_report_deliveries_status',
      sql`${t.status} IN ('pending','claimed','sending','sent','failed','rejected','ambiguous','missed')`,
    ),
  ],
)

export type InsertReportDelivery = typeof reportDeliveries.$inferInsert
export type SelectReportDelivery = typeof reportDeliveries.$inferSelect
